import { UserOutlined } from '@ant-design/icons';
import { App as AntApp, Form, Input, Modal, Typography } from 'antd';
import { useState } from 'react';

import { api, ApiRequestError } from '../lib/api.ts';

interface LoginValues {
  username: string;
  password: string;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  onSuccess: (email: string) => void;
}

/**
 * Email + password sign-in for the etravel account. Credentials are posted to
 * our own backend, which authenticates against etravel and keeps the token —
 * the browser never talks to etravel directly.
 */
export function LoginModal({ open, onCancel, onSuccess }: Props) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<LoginValues>();
  const [busy, setBusy] = useState(false);

  const submit = async (v: LoginValues) => {
    setBusy(true);
    try {
      const res = await api.login(v.username.trim(), v.password);
      message.success(`Signed in as ${res.email}`);
      form.resetFields();
      onSuccess(res.email);
    } catch (err) {
      message.error(err instanceof ApiRequestError ? err.message : err instanceof Error ? err.message : String(err), 6);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Sign in to etravel"
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText="Sign in"
      confirmLoading={busy}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Use your etravel account — the same email and password as the mobile app. Credentials go to this app&apos;s backend, which signs in
        to etravel for you.
      </Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={(v) => void submit(v)} requiredMark={false}>
        <Form.Item name="username" label="Email" rules={[{ required: true, type: 'email', message: 'Enter your account email' }]}>
          <Input autoComplete="username" placeholder="you@example.com" prefix={<UserOutlined />} />
        </Form.Item>
        <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Enter your password' }]}>
          <Input.Password autoComplete="current-password" placeholder="Password" onPressEnter={() => form.submit()} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
